import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { COLORS, Row, Col, Separator, TextInput } from '@cuemath/comps';
import { Text, Button } from '@cuemath/web-comps';

import { closeModal } from 'actions/modal';
import {
  usePaymentFlowOverridesViewGet,
  usePaymentFlowOverridesPatch,
  invalidatePaymentFlowOverridesView,
} from 'actions/payment-flow-overrides';

const sameDurations = (a, b) => {
  if (a.length !== b.length) return false;
  const sortedB = [...b].sort((x, y) => x - y);
  return [...a].sort((x, y) => x - y).every((v, i) => v === sortedB[i]);
};

const Container = styled(Col)`
  padding: 24px;
  gap: 8px;
`;

const Banner = styled(Row)`
  background: ${props => (props.$error ? '#fdecea' : '#fff6e5')};
  border: 1px solid ${props => (props.$error ? '#f5c6cb' : '#ffe0a3')};
  border-radius: 6px;
  padding: 10px 12px;
`;

const Segmented = styled(Row)`
  gap: 8px;
  flex-wrap: wrap;
`;

const Seg = styled.button`
  border: 1px solid ${props => (props.$active ? COLORS.CTA_LINK || '#1a73e8' : '#ccc')};
  background: ${props => (props.$active ? 'rgba(26,115,232,0.08)' : COLORS.WHITE)};
  color: ${props =>
    props.$active ? COLORS.CTA_LINK || '#1a73e8' : COLORS.TEXT || '#222'};
  border-radius: 6px;
  padding: 8px 18px;
  font-weight: 600;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.5 : 1)};
`;

const ReadOnly = styled(Text)`
  color: ${COLORS.GREY_2 || '#666'};
`;

const Footer = styled(Row)`
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
`;

function RenewalPlanDurationsModal({ student }) {
  const dispatch = useDispatch();
  const close = () => dispatch(closeModal());
  const studentId = student && student.id;

  const {
    data: view,
    get: getView,
    isProcessing: loading,
    isProcessingFailed: loadFailed,
  } = usePaymentFlowOverridesViewGet(studentId);

  useEffect(() => {
    if (studentId) getView(studentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const durationsCfg = view?.durations || null;
  // Card-EMI rides in the same view payload and the same PATCH. India-only:
  // is_applicable is false elsewhere, so the control hides itself.
  const cardEmiCfg = view?.card_emi || null;
  const showCardEmi = !!cardEmiCfg?.is_applicable;
  // card_emi carries the override, but fall back to current_overrides so the
  // control still reflects a stored value on a backend without that block.
  const initialCardEmi = !!(
    cardEmiCfg?.show_card_emi_options_india ??
    view?.current_overrides?.show_card_emi_options_india
  );
  // The renewal durations offered to admins — the backend's canonical set (all
  // have active subscription plans in pricing). Older backend omits it, so fall
  // back to the standard six.
  const available = durationsCfg?.renewal_duration_choices || [1, 3, 6, 12, 18, 24];
  const initial = durationsCfg?.renewal_plan_durations || [];
  const canEdit = !!view?.permissions?.can_set_overrides;

  const [selected, setSelected] = useState([]);
  const [cardEmi, setCardEmi] = useState(false);
  const [reason, setReason] = useState('');
  const [formError, setFormError] = useState('');
  const [seeded, setSeeded] = useState(false);

  // Seed the selection once the view loads (initial renewal_plan_durations).
  useEffect(() => {
    if (view && !seeded) {
      setSelected(initial);
      setCardEmi(initialCardEmi);
      setSeeded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, seeded]);

  const toggle = d =>
    setSelected(prev => (prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]));

  const { patch, isProcessing: saving } = usePaymentFlowOverridesPatch({
    onComplete: error => {
      if (error) {
        setFormError(error);
      } else {
        invalidatePaymentFlowOverridesView();
        close();
      }
    },
  });

  const onSave = () => {
    setFormError('');
    const durationsChanged = !sameDurations(selected, initial);
    const cardEmiChanged = showCardEmi && cardEmi !== initialCardEmi;
    if (!durationsChanged && !cardEmiChanged) {
      setFormError('No change to save.');
      return;
    }
    if (!reason.trim()) {
      setFormError('A reason is required.');
      return;
    }
    // Send only what changed — an unmentioned toggle keeps its stored value.
    const payload = { reason: reason.trim() };
    if (durationsChanged) {
      payload.renewal_plan_durations = [...selected].sort((a, b) => a - b);
    }
    if (cardEmiChanged) payload.show_card_emi_options_india = cardEmi;
    patch(studentId, payload);
  };

  return (
    <Container>
      <Text variant="h5">Renewal plan durations</Text>
      <Separator height={2} transparent />
      <ReadOnly variant="caption">
        Choose which plan durations this parent sees at renewal. Leave all off to show the
        region defaults; the backend falls back to the defaults when the selected
        durations aren&apos;t available for this student.
      </ReadOnly>
      <Separator height={12} transparent />

      {loading ? (
        <ReadOnly variant="body2">Loading…</ReadOnly>
      ) : loadFailed ? (
        <Banner $error>
          <Text variant="body2">Couldn&apos;t load the renewal setup.</Text>
        </Banner>
      ) : (
        <>
          {!canEdit && (
            <Banner>
              <Text variant="body2">
                You are not on the payment-flow-overrides allow-list — read-only.
              </Text>
            </Banner>
          )}
          <Segmented>
            {available.map(d => (
              <Seg
                key={d}
                type="button"
                $active={selected.includes(d)}
                disabled={!canEdit}
                onClick={() => canEdit && toggle(d)}
              >
                {d} mo
              </Seg>
            ))}
          </Segmented>

          {showCardEmi && (
            <>
              <Separator height={20} transparent />
              <Text variant="body2Bold">Card EMI options (India)</Text>
              <Separator height={6} transparent />
              <ReadOnly variant="caption">
                Off by default. Turn on to offer card EMI to this parent on the payment
                screen — it applies to renewals and post-trial payments alike.
              </ReadOnly>
              <Separator height={8} transparent />
              <Segmented>
                <Seg
                  type="button"
                  aria-pressed={!cardEmi}
                  $active={!cardEmi}
                  disabled={!canEdit}
                  onClick={() => canEdit && setCardEmi(false)}
                >
                  Off
                </Seg>
                <Seg
                  type="button"
                  aria-pressed={cardEmi}
                  $active={cardEmi}
                  disabled={!canEdit}
                  onClick={() => canEdit && setCardEmi(true)}
                >
                  On
                </Seg>
              </Segmented>
            </>
          )}

          <Separator height={16} transparent />
          <Text variant="body2Bold">Reason (required)</Text>
          <TextInput
            placeholder="Why is this override being set?"
            value={reason}
            onChange={setReason}
            disabled={!canEdit}
          />

          {formError && (
            <>
              <Separator height={10} transparent />
              <Banner $error>
                <Text variant="body2">{formError}</Text>
              </Banner>
            </>
          )}

          <Footer>
            <Button variant="secondary" size="small" label="Cancel" onClick={close} />
            <Button
              size="small"
              label={saving ? 'Saving…' : 'Save'}
              onClick={onSave}
              disabled={!canEdit || saving}
            />
          </Footer>
        </>
      )}
    </Container>
  );
}

export default RenewalPlanDurationsModal;
